import { type InputHTMLAttributes, type ReactNode, type Ref, type TextareaHTMLAttributes, useId } from 'react';

import {
  type BaseFormFieldContainerProps,
  FormFieldContainer,
  type FormFieldContainerStyleClasses,
} from '@/modus/FormFieldContainer';
import { cn, removePrefix } from '@/utils/styles';
import type { PrefixKeys, StyleClasses } from '@/utils/types';

export type InputNextStyleClasses = StyleClasses<'root' | 'input' | 'startAdornment' | 'endAdornment'>;

type BaseInputProps = {
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  multiline?: boolean;
  variant?: 'default' | 'borderless';
  classes?: InputNextStyleClasses & PrefixKeys<FormFieldContainerStyleClasses, 'formField'>;
} & BaseFormFieldContainerProps;

export type InputNextProps =
  | (InputHTMLAttributes<HTMLInputElement> & BaseInputProps & { multiline?: false; ref?: Ref<HTMLInputElement> })
  | (TextareaHTMLAttributes<HTMLTextAreaElement> &
      BaseInputProps & { multiline: true; ref?: Ref<HTMLTextAreaElement> });
export const InputNext = ({
  id,
  startAdornment,
  endAdornment,
  multiline,
  variant = 'default',
  className,
  classes,
  label,
  description,
  message,
  status,
  requirementType,
  ref,
  ...props
}: InputNextProps) => {
  const internalId = useId();
  const Component = multiline ? 'textarea' : 'input';

  const formFieldClasses = removePrefix(classes ?? {}, 'formField.');

  return (
    <FormFieldContainer
      id={id ?? internalId}
      label={label}
      description={description}
      message={message}
      status={status}
      requirementType={requirementType}
      classes={formFieldClasses}
    >
      <div
        className={cn(
          'flex w-full gap-3 rounded-lg border border-white/20 bg-transparent',
          'focus-within:border-white/30 focus-within:ring-0 focus-within:ring-white/30',
          'text-sm shadow-xs transition-colors placeholder:text-muted-foreground',
          'focus-visible:border-white/50 focus-visible:outline-hidden focus-visible:ring-0 focus-visible:ring-white/50',
          'file:text-foreground file:border-0 file:bg-transparent file:text-sm file:font-medium',
          'disabled:cursor-not-allowed disabled:opacity-50',
          variant === 'borderless' && 'border-none focus-within:border-none focus-within:ring-0',
          startAdornment && 'pl-3',
          endAdornment && 'pr-3',
          className,
          classes?.root,
        )}
      >
        {startAdornment && (
          <div className={cn('flex shrink-0 items-center text-gray-500', classes?.startAdornment)}>
            {startAdornment}
          </div>
        )}
        <Component
          id={id ?? internalId}
          ref={ref as Ref<any>}
          autoComplete="off"
          {...(props as any)}
          className={cn(
            'flex-1 resize-none bg-transparent px-3 py-2 outline-hidden placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
            'file:text-foreground file:border-0 file:bg-transparent file:text-sm file:font-medium',
            'focus-visible:outline-hidden focus-visible:ring-0',
            variant === 'borderless' && 'border-none focus-within:border-none focus-within:ring-0',
            startAdornment && 'pl-0',
            endAdornment && 'pr-0',
            classes?.input,
          )}
        />
        {endAdornment && (
          <div className={cn('flex shrink-0 items-center pr-3 text-gray-500', classes?.endAdornment)}>
            {endAdornment}
          </div>
        )}
      </div>
    </FormFieldContainer>
  );
};

InputNext.displayName = 'InputNext';
